import { DateTime } from 'luxon';
import compareAsc from 'date-fns/compareAsc';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import useUser from './hooks/useUser';
import type { Slot } from '../vl-common/src/types/index';

export const baseGetSlotBucketsQueryKey = ['bookingAppointmentSlots'];

function getSlotBuckets(timeSlotsData: Slot[], slotsTimezone: string) {
  if (!timeSlotsData || timeSlotsData.length === 0) {
    return { buckets: [], timeSlotsData: [] };
  }

  // slots are in UTC and sorted, bucket them by day in the local timezone
  const zonedSlots = timeSlotsData.map(({ slot_datetime, slot_end_datetime, clin_count }) => ({
    slot_datetime: DateTime.fromJSDate(new Date(slot_datetime)).setZone(slotsTimezone),
    slot_end_datetime: DateTime.fromJSDate(new Date(slot_end_datetime)).setZone(slotsTimezone),
    clin_count
  }));
  const buckets = zonedSlots
    .slice(1)
    .reduce(
      (acc, slot) => {
        const [[{ slot_datetime }]] = acc;

        if (!slot_datetime.hasSame(slot.slot_datetime, 'day')) {
          return [[slot], ...acc];
        }

        acc[0].push(slot);
        return acc;
      },
      [[zonedSlots[0]]]
    )
    .reverse();

  return {
    buckets,
    timeSlotsData: buckets.map((b) => ({ dom: b[0].slot_datetime, dow: b[0].slot_datetime, slots: b }))
  };
}

const useClinicianSlots = (
  week_offset: number,
  clin_subtype: string,
  clin_specarea: string[],
  patient_guid: string,
  clin_guid: string,
  clin_speccat: string,
  appt_type_code: string,
  start: Date | null,
  end: Date | null
): {
  startDay?: DateTime | null;
  buckets?: { start: Date; end: Date }[];
} => {
  const dispatch = useDispatch();
  const user = useUser();
  const tz = user.timezone;

  const { data: bookingData } = useQuery({
    queryKey: [
      ...baseGetSlotBucketsQueryKey,
      { week_offset, clin_subtype, clin_specarea, patient_guid, clin_guid, clin_speccat, appt_type_code, start, end }
    ],
    queryFn: () =>
      dispatch(
        actions.rescheduleCalendar({
          week_offset,
          clin_subtype,
          clin_specarea: Array.isArray(clin_specarea) ? clin_specarea.join() : [clin_specarea].join(','),
          patient_guid,
          clin_guid,
          clin_speccat,
          appt_type_code,
          start: start && start > new Date() ? start.toISOString?.() || start.toString() : '',
          end: end ? end.toISOString?.() || end.toString() : ''
        }),
        true
      ),
    select: (data) => {
      const payload = data.payload || [];
      const earliestDay = payload.reduce(
        (earliest, slot) => {
          if (!slot?.dom) return earliest;
          const slotDateTime = DateTime.fromJSDate(new Date(slot.dom)).setZone(tz).startOf('day');
          if (!earliest) return slotDateTime;
          return slotDateTime < earliest ? slotDateTime : earliest;
        },
        null as DateTime | null
      );

      const slotBuckets = getSlotBuckets(
        data.payload
          .flatMap((a) => a.slots)
          .filter((n) => n)
          .sort(({ slot_datetime: lhs }, { slot_datetime: rhs }) => compareAsc(new Date(lhs), new Date(rhs))),
        tz
      )
        .buckets.flat()
        .map(({ slot_datetime, slot_end_datetime }) => ({
          start: slot_datetime.toJSDate(),
          end: slot_end_datetime.toJSDate()
        }));
      return {
        startDay: earliestDay,
        buckets: slotBuckets || []
      };
    }
  });

  return { ...bookingData };
};

export { useClinicianSlots, getSlotBuckets };
export default getSlotBuckets;
